import React, { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import Export from "../../assets/dashboard/export.svg";
import impression from "../../assets/dashboard/impression.svg";
import engagement from "../../assets/dashboard/engagement.svg";
import followers from "../../assets/dashboard/followers.svg";
import add from "../../assets/dashboard/plus.svg";
import sub from "../../assets/dashboard/minus.svg";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Insights = () => {
  const [activeTab, setActiveTab] = useState("Impressions");
  const [showSeniority, setShowSeniority] = useState(false);
  const [showJobFunctions, setShowJobFunctions] = useState(false);

  const toggleSeniority = () => setShowSeniority(!showSeniority);
  const toggleJobFunctions = () => setShowJobFunctions(!showJobFunctions);

  const tabs = [
    { label: "Impressions", icon: impression },
    { label: "Engagements", icon: engagement },
    { label: "Followers", icon: followers },
  ];

  const labels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
  ];

  const data = {
    labels,
    datasets: [
      {
        label: "Current period vs.",
        data: [150, 1900, 1400, 1100, 850, 700, 1500],
        borderColor: "#fef3c0",
        backgroundColor: "#fdd835",
        tension: 0.3,
      },
      {
        label: "Previous period",
        data: [120, 600, 300, 500, 800, 500, 1100],
        borderColor: "#caf4f5",
        backgroundColor: "#55dde0",
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 2000,
        ticks: {
          stepSize: 200,
        },
      },
    },
  };

  const seniorityData = [
    { label: "Senior", value: 125 },
    { label: "Entry-level", value: 120 },
    { label: "Director", value: 100 },
    { label: "Manager", value: 80 },
  ];

  const jobFunctionData = [
    { label: "Marketing", value: 150 },
    { label: "Sales", value: 100 },
    { label: "Media & Communications", value: 90 },
    { label: "Human Resources", value: 50 },
  ];

  // Helper function to get bar width based on max value
  const getBarWidth = (value, dataArray) => {
    const maxValue = Math.max(...dataArray.map((item) => item.value));
    return `${(value / maxValue) * 100}%`;
  };

  return (
    <div className="border border-gradient-04 rounded-2xl p-2 md:p-4 shadow-sm bg-white relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 border-b border-black-40">
        <h2 className="font-bold text-lg text-black-800 uppercase">
          Insights Last 90 Days
        </h2>
        <button className="p-3 mb-2 bg-black-0 hover:bg-black-20 focus:bg-black-40 border-2 border-black-600 rounded group relative">
          <img src={Export} alt="Export" />
          <div className="absolute top-12 left-1/2 -translate-x-1/2 mr-2 px-2 py-1 bg-gray-800 text-white text-xs shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 whitespace-nowrap uppercase">
            Export
            <div className="absolute left-1/2 bottom-full border-4 border-transparent border-b-gray-800"></div>
          </div>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 md:gap-4 mb-4 bg-black-30 border-2 border-black-30 rounded-md w-full items-center justify-center">
        {tabs.map(({ label, icon }) => (
          <button
            key={label}
            onClick={() => setActiveTab(label)}
            className={`flex items-center justify-center gap-1 md:gap-2 py-2 px-1 md:py-4 md:px-12 text-xs md:text-lg relative ${
              activeTab === label ? "bg-black-0 rounded-md" : ""
            }`}
          >
            <img src={icon} alt={label} className="w-4 h-4 hidden md:block" />
            {label}
          </button>
        ))}
      </div>

      {/* Metric summary */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-success-500 font-bold text-xs bg-black-20 rounded px-2 py-0.5">
          1400
        </span>
        <span className="text-success-500 text-xs">↑ 5%</span>
      </div>

      {/* Chart */}
      <Line data={data} options={options} />

      {/* Expandable sections */}
      <div className="mt-6 text-lg font-bold text-black-600">
        <div className="border-b border-black-600">
          <button
            onClick={toggleSeniority}
            className="flex justify-between w-full py-2"
          >
            <span className="">Audience by seniority level</span>
            {showSeniority ? (
              <img src={sub} alt="Minus" />
            ) : (
              <img src={add} alt="Plus" />
            )}
          </button>
          {showSeniority && (
            <div className="my-4 space-y-4">
              {seniorityData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 w-1/3 min-w-[120px]">
                      {item.label}
                    </span>

                    <span className="text-sm text-gray-700 text-right ml-auto">
                      {item.value}
                    </span>
                  </div>
                  <div className="w-full relative bg-black-20">
                    <div
                      className="h-2 bg-primary-300 rounded"
                      style={{
                        width: getBarWidth(item.value, jobFunctionData),
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-b border-black-600">
          <button
            onClick={toggleJobFunctions}
            className="flex justify-between w-full py-2"
          >
            <span className="">Audience by top job functions</span>
            {showJobFunctions ? (
              <img src={sub} alt="Minus" />
            ) : (
              <img src={add} alt="Plus" />
            )}
          </button>
          {showJobFunctions && (
            <div className="my-4 space-y-4">
              {jobFunctionData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 w-1/3 min-w-[120px]">
                      {item.label}
                    </span>

                    <span className="text-sm text-gray-700 text-right ml-auto">
                      {item.value}
                    </span>
                  </div>
                  <div className="w-full relative bg-black-20">
                    <div
                      className="h-2 bg-primary-300 rounded"
                      style={{
                        width: getBarWidth(item.value, jobFunctionData),
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Insights;
